import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TourMascot, { TourStepProgress } from './TourMascot.jsx';

const PAD = 8;
const TIP_GAP = 10;

function getBottomNavH() {
    const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--bottom-nav-height')
        .trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 64;
}

function usableBottom() {
    const nav = getBottomNavH();
    const safe = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--safe-bottom'),
    ) || 0;
    // 1023.98 must match the desktop cutoff in useIsDesktop.js (minWidth: 1024)
    // and theme.css's `.bottom-nav` visibility break — that's where the nav
    // actually disappears. This is a plain function called from imperative
    // scroll/measure code outside React's render cycle, so useIsDesktop (a
    // hook) doesn't fit here; the literal is duplicated on purpose.
    const isMobile = window.matchMedia('(max-width: 1023.98px)').matches;
    return window.innerHeight - (isMobile ? nav + safe + 12 : 16);
}

function measure(target) {
    const els = document.querySelectorAll(`[data-tour="${target}"]`);
    for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.width < 2 || r.height < 2) continue;
        const style = window.getComputedStyle(el);
        if (style.visibility === 'hidden' || style.display === 'none') continue;
        return {
            top: Math.max(0, r.top - PAD),
            left: Math.max(0, r.left - PAD),
            width: r.width + PAD * 2,
            height: r.height + PAD * 2,
            el,
        };
    }
    return null;
}

function scrollPageToTop() {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const main = document.querySelector('.app-main');
    if (main) main.scrollTop = 0;
}

/** Scroll so el sits high enough that a tip can fit below it, above the bottom nav. */
function scrollToReveal(el, tipHeight) {
    document.body.style.overflow = '';

    const topLimit = 72;
    const bottomLimit = usableBottom();
    const tipSpace = tipHeight + TIP_GAP + 8;
    const maxTargetBottom = bottomLimit - tipSpace;
    const r = el.getBoundingClientRect();

    let delta = 0;
    if (r.top < topLimit) {
        delta = r.top - topLimit;
    } else if (r.bottom > maxTargetBottom) {
        delta = r.bottom - maxTargetBottom;
    }

    if (Math.abs(delta) > 8) {
        const main = document.querySelector('.app-main');
        if (main && main.scrollHeight > main.clientHeight + 1) {
            main.scrollTop += delta;
        } else {
            window.scrollBy(0, delta);
        }
    }
}

function tipHoriz(hole) {
    if (!hole || typeof window === 'undefined') return {};
    const gutter = 16;
    const tipW = Math.min(420, window.innerWidth - gutter * 2);
    const ideal = hole.left + hole.width / 2 - tipW / 2;
    const left = Math.min(Math.max(gutter, ideal), window.innerWidth - tipW - gutter);
    return {
        left: `${left}px`,
        right: 'auto',
        marginInline: 0,
        width: `${tipW}px`,
        maxWidth: 'none',
    };
}

function placeTip(hole, tipHeight, tipCeiling) {
    const below = hole.top + hole.height + TIP_GAP;
    if (below + tipHeight <= tipCeiling) {
        return { top: below, placement: 'below' };
    }
    const above = hole.top - tipHeight - TIP_GAP;
    if (above >= 12) {
        return { top: above, placement: 'above' };
    }
    // Tip and hole cannot both fit; pin the tip to whichever edge leaves
    // the hole more visible. placement still describes tip-vs-hole so the
    // bubble tail points at the cutout.
    if (hole.top > window.innerHeight * 0.4) {
        return { top: 12, placement: 'above' };
    }
    return { top: Math.max(12, tipCeiling - tipHeight), placement: 'below' };
}

export default function TourSpotlight({
    target,
    stepId,
    pose,
    stepIndex,
    stepCount,
    onNext,
    onSkip,
    isLast,
}) {
    const { t } = useTranslation('tour');
    const title = t(`steps.${stepId}.title`);
    const body = t(`steps.${stepId}.body`);
    const [rect, setRect] = useState(null);
    const [tipHeight, setTipHeight] = useState(168);
    const [tailX, setTailX] = useState(null);
    const tipRef = useRef(null);
    const observedElRef = useRef(null);
    const tipHeightRef = useRef(168);

    useLayoutEffect(() => {
        let cancelled = false;
        let tries = 0;
        let ro = null;
        const prevOverflow = document.body.style.overflow;

        const applyRect = (next) => {
            if (cancelled || !next) return;
            setRect(next);

            if (observedElRef.current !== next.el) {
                ro?.disconnect();
                observedElRef.current = next.el;
                if (typeof ResizeObserver !== 'undefined') {
                    ro = new ResizeObserver(() => {
                        if (cancelled) return;
                        const again = measure(target);
                        if (!again) return;
                        const tipH = tipHeightRef.current;
                        const maxBottom = usableBottom() - tipH - TIP_GAP;
                        if (again.top + again.height > maxBottom || again.top < 64) {
                            scrollToReveal(again.el, tipH);
                            requestAnimationFrame(() => {
                                if (!cancelled) setRect(measure(target));
                            });
                        } else {
                            setRect(again);
                        }
                    });
                    ro.observe(next.el);
                    if (next.el.parentElement) ro.observe(next.el.parentElement);
                }
            }
        };

        const tick = () => {
            if (cancelled) return;
            const next = measure(target);
            if (next) {
                const tipH = tipHeightRef.current;
                const maxBottom = usableBottom() - tipH - TIP_GAP;
                const needsScroll = next.top < 64 || next.top + next.height > maxBottom;
                document.body.style.overflow = '';
                if (needsScroll) {
                    scrollToReveal(next.el, tipH);
                    requestAnimationFrame(() => {
                        if (cancelled) return;
                        document.body.style.overflow = 'hidden';
                        applyRect(measure(target));
                    });
                } else {
                    document.body.style.overflow = 'hidden';
                    applyRect(next);
                }
                return;
            }
            tries += 1;
            if (tries < 60) setTimeout(tick, 50);
            else {
                document.body.style.overflow = 'hidden';
                setRect(null);
            }
        };

        setRect(null);
        observedElRef.current = null;
        scrollPageToTop();
        document.body.style.overflow = '';
        requestAnimationFrame(tick);

        const onResize = () => {
            const next = measure(target);
            if (next) setRect(next);
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onResize, true);

        return () => {
            cancelled = true;
            ro?.disconnect();
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onResize, true);
        };
    }, [target]);

    useLayoutEffect(() => {
        if (!tipRef.current) return;
        const box = tipRef.current.getBoundingClientRect();
        const h = box.height || 168;
        tipHeightRef.current = h;
        setTipHeight(h);
        if (rect) {
            const holeCenter = rect.left + rect.width / 2;
            const x = Math.min(Math.max(holeCenter - box.left, 20), Math.max(20, box.width - 20));
            setTailX(x);
        } else {
            setTailX(null);
        }
    }, [title, body, rect, stepIndex, pose]);

    const hole = rect;
    const tipCeiling = usableBottom();
    const placed = hole
        ? placeTip(hole, tipHeight, tipCeiling)
        : { top: '18%', placement: 'below' };
    const tipStyle = {
        top: placed.top,
        ...(hole ? tipHoriz(hole) : {}),
        ...(tailX != null ? { '--tour-tail-x': `${tailX}px` } : {}),
    };

    return (
        <div className="tour-root" role="dialog" aria-modal="true" aria-label={title}>
            <div
                className="tour-scrim"
                style={{ background: hole ? 'transparent' : 'var(--color-scrim)' }}
                onClick={onSkip}
            />
            {hole && (
                <div
                    className="tour-hole"
                    style={{
                        top: hole.top,
                        left: hole.left,
                        width: hole.width,
                        height: hole.height,
                    }}
                />
            )}
            <div
                className={`tour-tip tour-tip--${placed.placement}`}
                ref={tipRef}
                style={tipStyle}
            >
                <div className="tour-tip-copy">
                    <TourMascot pose={pose} size="tip" />
                    <div className="tour-tip-text">
                        <div className="tour-tip-title">{title}</div>
                        <p className="tour-tip-body">{body}</p>
                    </div>
                </div>
                <div className="tour-tip-actions">
                    <button type="button" className="tour-skip" onClick={onSkip}>
                        {t('skip')}
                    </button>
                    <TourStepProgress stepIndex={stepIndex} stepCount={stepCount} />
                    <button type="button" className="btn-ms btn-ms-primary btn-ms-sm" onClick={onNext}>
                        {isLast ? t('finish') : t('next')}
                    </button>
                </div>
            </div>
        </div>
    );
}
